import { biEvents } from '#packages/coreBi';
import { arrayUtils } from '#packages/util';
import {
  getPinDockingDirection as getPinDockingDirection_inner,
  getPinDockingOuterOffsets as getPinDockingOuterOffsets_inner,
  calcPinDockingOffsetsDefaults,
  createLayoutDockingByPinDockingDirection,
  createPopupContainerPropertiesByPinDockingDirection,
  type PinDockingOptions,
  type PinDockingDirection,
  type PinDockingOuterOffsets,
} from '#packages/pinModeUtils';
import { isPopupContainer } from '#packages/documentServices';
import type { EditorAPI } from '#packages/editorAPI';
import type {
  CompLayout,
  CompRef,
  Docking,
  PopupContainerProperties,
} from 'types/documentServices';
import type { LayoutGetApi } from '../layoutGetApi';
import type { LayoutFixedPositionApi } from './createLayoutFixedPositionApi';
import type { LayoutDockApi } from './createLayoutDockApi';

export function createLayoutPinApi({
  editorAPI,
  layoutGetApi,
  layoutDockApi,
  layoutFixedPositionApi,
}: {
  editorAPI: EditorAPI;
  layoutGetApi: LayoutGetApi;
  layoutDockApi: LayoutDockApi;
  layoutFixedPositionApi: LayoutFixedPositionApi;
}) {
  async function pinInner_defaultDocking(compRef: CompRef, docking: Docking) {
    if (!isPinned(compRef)) {
      layoutFixedPositionApi.setFixed(compRef, true);
    }

    layoutDockApi.setDock(compRef, docking);
  }

  async function pinToDirectionInner_defaultPopupProperties(
    compRef: CompRef,
    { dockingDirection, dockingOffsets }: PinDockingOptions,
  ) {
    const properties =
      editorAPI.dsRead.components.properties.get<PopupContainerProperties>(
        compRef,
      );

    const propertiesUpdated =
      createPopupContainerPropertiesByPinDockingDirection(
        properties.alignmentType,
        {
          dockingDirection,
          dockingOffsets,
        },
      );
    editorAPI.components.properties.update(compRef, propertiesUpdated);
    // TODO: do we really need to update the mobile component additionally?
    // https://github.com/wix-private/santa-editor/blob/f8594982c4d585e2316802b665c5c958e97a7ad3/santa-editor/packages/compPanels/dynamicPanels/popupGridLayoutPanel/popupGridLayoutPanel.tsx#L136
    // source: https://github.com/wix-private/santa-editor/commit/4c836d3557c9275beced14c37a8cce81e959923a#diff-a9f1991640f6d268b3615517f8827f8c287b00b10eada3977aca58fa8eee4f3dR119
    const compRefMobile = editorAPI.components.get.byId(
      compRef.id,
      editorAPI.components.getPage(compRef).id,
      editorAPI.viewMode.VIEW_MODES.MOBILE,
    );
    editorAPI.components.properties.update(compRefMobile, propertiesUpdated);
  }

  async function pinToDirectionInner_defaultDocking(
    compRef: CompRef,
    { dockingDirection, dockingOffsets }: PinDockingOptions,
  ) {
    const compLayout = layoutGetApi.get(compRef);
    const docking = createLayoutDockingByPinDockingDirection(compLayout, {
      dockingDirection,
      dockingOffsets,
    });

    pinInner_defaultDocking(compRef, docking);
  }

  async function pinToDirection(
    compRef: CompRef,
    {
      dockingDirection,
      dockingOffsets,
      biOrigin,
    }: PinDockingOptions & {
      biOrigin?: string;
    },
  ) {
    const isPinnedCurrent = isPinned(compRef);

    dockingOffsets =
      // if component
      // 1. is already pinned, and direction is not changed,
      // use current offsets (+ `dockingOffsets` if provided)
      // 2. is not pinned, or direction is changed,
      // use default offsets (+ `dockingOffsets` if provided)
      isPinnedCurrent && getPinDockingDirection(compRef) === dockingDirection
        ? {
            ...getPinDockingOffsets(compRef),
            ...dockingOffsets,
          }
        : {
            ...calcPinDockingOffsetsDefaults(dockingDirection, 0),
            ...dockingOffsets,
          };

    if (!isPinnedCurrent) {
      editorAPI.bi.event(biEvents.pinToScreen.FIRST_PIN_TO_SCREEN, {
        component_id: compRef.id,
        component_type: editorAPI.components.getType(compRef),
        selected_area: dockingDirection,
        origin: biOrigin,
      });
    }

    if (isPopupContainer(editorAPI.dsRead, compRef)) {
      await pinToDirectionInner_defaultPopupProperties(compRef, {
        dockingDirection,
        dockingOffsets,
      });
      return;
    }

    await pinToDirectionInner_defaultDocking(compRef, {
      dockingDirection,
      dockingOffsets,
    });

    // TODO: move history.add from the pinnedDockPanel and popupGridLayoutPanel to here
  }

  function couldBeUnPinned(compRef: CompRef) {
    if (isPopupContainer(editorAPI.dsRead, compRef)) {
      return false;
    }

    return true;
  }

  async function unPin(
    compRef: CompRef,
    {
      biOrigin,
    }: {
      biOrigin?: string;
    } = {},
  ) {
    if (!isPinned(compRef)) {
      return;
    }

    if (!couldBeUnPinned(compRef)) {
      throw new Error(`Component ${compRef.id} can't be unpinned.`);
    }

    layoutDockApi.unDock(compRef);
    layoutFixedPositionApi.setFixed(compRef, false);

    editorAPI.bi.event(biEvents.pinToScreen.UNPIN, {
      origin: biOrigin,
      component_type: editorAPI.components.getType(compRef),
      component_id: compRef.id,
    });

    // TODO: move history.add from the pinnedDockPanel to here
  }

  /**
   * @deprecated Use isPinned instead
   * https://wix.slack.com/archives/C04EX7N2BCP/p1695403836550189
   */
  function isPinnedByLayout_deprecated(serializedLayout: Partial<CompLayout>) {
    // - streatched
    // left/rigt
    // top/bottom
    // - fixed
    return (
      typeof serializedLayout.docked !== 'undefined' &&
      serializedLayout.fixedPosition
    );
  }

  function isPinned(compRef: CompRef): boolean {
    if (isPopupContainer(editorAPI.dsRead, compRef)) {
      return true;
    }

    const serializedLayout = editorAPI.dsRead.components.layout.get(compRef);
    // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
    const docked = editorAPI.dsRead.components.layout.getDock(compRef);

    return isPinnedByLayout_deprecated({
      ...serializedLayout,
      docked,
    });
  }

  /**
   * Editor UI expects that `isPinned` returns `true` only for the cases when old `layout.docked` was defined
   * ```
   * isPinned: layout.fixedPosition === true && layout.docked !== undefined
   * ```
   * With the new layout structure `FixedItemLayout`, we need to preserve this behaviour.
   */
  function isPinned_backwarCompatible(compRefOrRefs: CompRef | CompRef[]) {
    const compRefs = arrayUtils.asArray(compRefOrRefs);

    return compRefs.every((compRef) => {
      if (editorAPI.components.is.fixedNonPinned(compRefs)) {
        return false;
      }

      if (isPopupContainer(editorAPI.dsRead, compRef)) {
        return false;
      }

      return isPinned(compRef);
    });
  }

  function getPinDockingDirection(compRef: CompRef): PinDockingDirection {
    return getPinDockingDirection_inner(editorAPI, compRef);
  }

  function getPinDockingOffsets(compRef: CompRef): PinDockingOuterOffsets {
    return getPinDockingOuterOffsets_inner(editorAPI, compRef);
  }

  return {
    pinToDirection,
    unPin,
    isPinned: isPinned_backwarCompatible,
    isPinnedByLayout_deprecated,
    getPinDockingDirection,
    getPinDockingOffsets,
  };
}

export type LayoutPinApi = ReturnType<typeof createLayoutPinApi>;
